import { useParams } from 'react-router-dom';

import { useAppSelector } from '@/store/hooks';
import {
  getWorkingProjectSessions,
  selectWorkingProjectStatus,
  selectWorkingSession,
} from '@/store/slices/project/selectors';
import OptionalComponent from '@ui/optional-component/optional-component';
import SessionHeaderRow from './components/session-header-row/session-header-row';
import SessionStatsRow from './components/stats-row/session-stats/session-stats-row';
import GreenroomShotsRow from './components/greenroom-shots-row/greenroom-shots-row';
import useResourcePool, { ResourcePoolContext } from '@/utils/greenroom/use-resource-pool';
import { EBroadcastTypes } from '@/types/session.types';
import { EProjectStatus } from '@/types/projects.types';
import Container from '@ui/container/container';
import EditSessionImage from './components/edit-session-image/edit-session-image';
import EditSessionTitle from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/edit-session-title/edit-session-title';
import SessionStreamInfo from './components/session-stream-info/session-stream-info';
import EditTags from './components/edit-tags/edit-tags';
import EditSessionLanguage from './components/edit-session-language/edit-session-language';
import useBlockNavigationWhileSavingProject from './use-block-navigation-while-saving-project';
import EditSessionCategories from './components/edit-session-categories/edit-session-categories';
import EditSessionDescription from './components/edit-session-description/edit-session-description';
import EditSessionFeatures from './components/edit-session-features/edit-session-features';
import VideoSettings from './components/video-settings/video-settings';
import ProjectEventTypePageSkeleton from './components/project-event-type-page-skeleton/project-event-type-page-skeleton';
import useLoadSession from '@/connection/hooks/use-load-session';
import CastAndCrewSelectWithInvites from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/cast-and-crew/cast-and-crew-select-with-invites/cast-and-crew-select-with-invites';

const ProjectSession = () => {
  const resourcePool = useResourcePool();

  const sessions = useAppSelector(getWorkingProjectSessions);
  const session = useAppSelector(selectWorkingSession);
  const projectStatus = useAppSelector(selectWorkingProjectStatus);
  const params = useParams();

  const sessionUuid = params?.sessionUuid;
  const projectUuid = params?.projectUuid;

  const isPublished = projectStatus === EProjectStatus.PUBLIC;

  const sessionId = sessions?.find((s) => s.uuid === sessionUuid)?.session;

  useLoadSession(sessionId);

  useBlockNavigationWhileSavingProject();

  if (!session || !sessionUuid) {
    return <ProjectEventTypePageSkeleton />;
  }
  return (
    /*
      do not remove sessionUuid from key prop
      we need this to re-render the entire session overview when duplicating sessions and navigating to the new session,
      with the prop we'll end up with previous session data in the new session overview
    */
    <div data-component="ProjectSession" key={sessionUuid}>
      <div className="flex-column padding-h-10 gap-10 position-relative">
        <SessionHeaderRow />

        <EditSessionTitle />
      </div>
      <Container className="flex ai-flex-start gap-24" padding="0 10px" width="992px" margin="16px 0 0 0">
        <Container width="392px" className="flex-column gap-16">
          <EditSessionImage session={session} />

          <EditSessionFeatures session={session} />
        </Container>

        <Container className="flex-column gap-16 flex-1">
          {/* No mention of greenroom button in AC for BDEV-1849 so leaving in for now */}
          {/* <Container className="flex flex-1 ai-center jc-flex-end gap-16">
            <div>
              <SessionActionItems session={session} />
            </div>
          </Container> */}

          <Container>
            <SessionStreamInfo session={session}>
              {({ isFocusedWithin, setIsFocusedWithin }) => (
                <Container className="video-settings margin-t-8">
                  <VideoSettings
                    setIsFocusedWithin={setIsFocusedWithin}
                    isEditingInfo={isFocusedWithin}
                    session={session}
                  />
                </Container>
              )}
            </SessionStreamInfo>
          </Container>

          <Container width="576px" className="flex ai-flex-start as-stretch gap-20">
            <Container width="278px" flex="1 1 0">
              <EditSessionCategories sessionUuid={sessionUuid} />
            </Container>

            <Container width="278px">
              <EditTags sessionUuid={sessionUuid} />
            </Container>
          </Container>

          <Container>
            <EditSessionDescription sessionUuid={sessionUuid} />
          </Container>

          <Container width="570px" maxWidth="570px">
            <CastAndCrewSelectWithInvites sessionUuid={sessionUuid} />
          </Container>

          <Container>
            <EditSessionLanguage sessionUuid={sessionUuid} />
          </Container>
        </Container>
      </Container>

      <SessionStatsRow sessionUuid={sessionUuid} projectUuid={projectUuid} isPublished={isPublished} />

      {/* TODO: CalendarRow is post MVP */}
      {/* <CalendarRow session={session} /> */}

      <Container
        /*
          This Container is simply to add some height animation when showing/removing the shots row.
          The animation won't really be noticeable unless there is a component underneath it.
        */
        height="211px"
        maxHeight={session.broadcast_type === EBroadcastTypes.greenroom ? '211px' : '0'}
        className={session.broadcast_type === EBroadcastTypes.greenroom ? '' : 'overflow-hidden pointer-events-none'}
      >
        <OptionalComponent display={session.broadcast_type === EBroadcastTypes.greenroom}>
          <ResourcePoolContext.Provider value={resourcePool}>
            <GreenroomShotsRow sessionUuid={session.uuid} />
          </ResourcePoolContext.Provider>
        </OptionalComponent>
      </Container>
      {/* TODO: CalendarRow is post MVP */}
      {/* <UpNextRow session={session} /> */}
    </div>
  );
};

export default ProjectSession;
